import React, { Suspense } from 'react';
import ReactDOM from 'react-dom';
import App from './app/App';
import { persistor, store } from './app/store';
import { Provider } from 'react-redux';
import { Configuration, PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import * as serviceWorker from './serviceWorker';
import { msalConfig } from 'constants/msAuthConfig';
import { PersistGate } from 'redux-persist/es/integration/react';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from 'components/ErrorFallback';
import { loginWithMicrosoft } from './features/auth/loginAction';
import './index.scss';

declare global {
  interface Window {
    OneSignal: any;
  }
}

const msalInstance = new PublicClientApplication(msalConfig as Configuration);

const handleResponse = (response: any) => {
  if (response !== null) {
    const email = response.account ? response.account.username : '';
    const accessToken = response.accessToken;
    store.dispatch(loginWithMicrosoft({ email, accessToken }));
  }
};

msalInstance
  .handleRedirectPromise()
  .then(handleResponse)
  .catch((error) => {
    console.log(error);
  });

if (process.env.NODE_ENV === 'development') {
  window.addEventListener(
    'error',
    (event: ErrorEvent) => {
      const message = event.message || event.error?.message || '';
      if (typeof message === 'string' && message.includes('ResizeObserver loop')) {
        event.stopImmediatePropagation();
      }
    },
    true,
  );

  const originalOnError = window.onerror;
  window.onerror = function (
    message: string | Event,
    source?: string,
    lineno?: number,
    colno?: number,
    error?: Error,
  ): boolean {
    const text =
      (typeof message === 'string' ? message : '') ||
      (error && typeof error.message === 'string' ? error.message : '');
    if (text.includes('ResizeObserver loop')) {
      return true;
    }
    if (typeof originalOnError === 'function') {
      return originalOnError(message as any, source, lineno, colno, error) as boolean;
    }
    return false;
  };

  const originalConsoleError = console.error;
  console.error = (...args: any[]) => {
    try {
      const first = args[0];
      const allText = args
        .map((a) =>
          typeof a === 'string'
            ? a
            : a && a.message && typeof a.message === 'string'
            ? a.message
            : '',
        )
        .join(' ');
      if (typeof first === 'string' && first.includes('ResizeObserver loop')) {
        return;
      }
      if (allText.includes('ResizeObserver loop')) {
        return;
      }
    } catch (_e) {
      // ignore
    }
    originalConsoleError(...args);
  };

  const hideResizeObserverOverlay = () => {
    const overlay = document.getElementById('react-error-overlay');
    if (overlay && overlay.textContent && overlay.textContent.includes('ResizeObserver loop')) {
      overlay.style.display = 'none';
      overlay.style.pointerEvents = 'none';
    }

    const wdsOverlay = document.getElementById('webpack-dev-server-client-overlay');
    if (wdsOverlay && wdsOverlay instanceof HTMLElement) {
      wdsOverlay.style.display = 'none';
      wdsOverlay.style.pointerEvents = 'none';
    }
  };

  if (typeof MutationObserver !== 'undefined' && typeof document !== 'undefined' && document.body) {
    hideResizeObserverOverlay();
    const observer = new MutationObserver(hideResizeObserverOverlay);
    observer.observe(document.body, { childList: true, subtree: true });
  }
}

ReactDOM.render(
  <React.StrictMode>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <Suspense fallback={null}>
          <MsalProvider instance={msalInstance}>
            <ErrorBoundary
              FallbackComponent={ErrorFallback}
              onReset={() => {
                window.location.reload();
              }}
            >
              <App />
            </ErrorBoundary>
          </MsalProvider>
        </Suspense>
      </PersistGate>
    </Provider>
  </React.StrictMode>,
  document.getElementById('root'),
);

serviceWorker.unregister();
